/* Sprint 7B: Fuels-only multi-product block on the TradeRecap detail page.
 *
 * Renders ``formFields.products`` as a list of identical Product cards
 * (Product 1, Product 2, ...). Each card carries:
 *   - Stock catalogue picker (RecapProductSearch, fuels-filtered)
 *   - Fuel spec dropdowns: Fuel Type / Grade / Sulfur Limit / ISO 8217 Year
 *   - Per-product Measuring Unit
 *   - Sale Quantity, Sale Price, Buy Price
 *   - Supplier (CRM partner picker), Supplier Credit Days, Supplier Contract
 *
 * Mirrors the per-product card pattern already used on FuelEnquiryForm
 * and FuelOfferGenerator so the recap UX is consistent across the deal
 * lifecycle (Sprint 7B Q "make recap match deals").
 *
 * Mounted only when ``commodityType === 'fuels'``. Solids recaps keep
 * the legacy single-product inline scalars on TradeRecap.js.
 *
 * Pre-Sprint-7B history: this file used to render a single inline
 * "Bunker Specification" card for Product 1 + an extras section for
 * Product 2..N. Sprint 7B unifies the two so every product is rendered
 * the same way.
 */

import React, { useEffect, useState } from 'react';
import { fuelEnquiryService } from '../../services/fuelEnquiryService';
import {
  autofillSpecFromVariant,
} from '../../data/mixedFuelsSpec';
import CustomerPicker from '../FuelEnquiries/CustomerPicker';
import RecapProductSearch from '../RecapProductSearch';
import { lineQuantityUnit, perUnitLabel, unitLabel } from '../../utils/unitLabels';

import AmountInput from '../ui/AmountInput';
import { PRICE_DECIMALS, QUANTITY_DECIMALS } from '../../utils/precision';
import { withStoredOption } from '../../utils/paymentTerms';
import { productShorthand } from '../../utils/productShorthand';
import './FuelsSpecBlock.css';

// Only Lubrication-category variants are pickable on a lubes line, so a coal
// or fuel variant can never be sold as a drum of lube.
const LUBES_VARIANT_FILTER = (variant) => !!variant && variant.category === 'lubrication';

const FuelsSpecBlock = ({
  commodityType,
  formFields,
  // Every product edit goes through this. The recap passes a setter that also
  // marks the recap as changed (TradeRecap.js ``editFormFields``), so an edit
  // here arms the autosave and the leave-the-page warning like any other.
  setFormFields,
  onCreateProduct,
  // Bunkers Phase 1 (May 2026): leg-aware product card.
  // ``recapType`` switches the per-product price input between
  // Sell Price (SALE leg) and Purchase Price (PURCHASE leg).
  // ``paymentTermsOptions`` is the same dropdown source used by
  // the deal-level Payment Terms (TradeRecap.js loads it once from
  // /api/offer/payment_presets/ and forwards it here) -- since the
  // catalogue merge that is the ONE Payment Terms list every desk uses,
  // as [{value: term_code, label: description}].
  recapType = 'SALE',
  paymentTermsOptions = [],
}) => {
  const [lookups, setLookups] = useState({
    fuelTypes: [],
    fuelGrades: [],
    sulfurLimits: [],
    iso8217Years: [],
    measuringUnits: [],
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (commodityType !== 'fuels') return;
    let cancelled = false;
    (async () => {
      try {
        const [
          fuelTypes,
          fuelGrades,
          sulfurLimits,
          iso8217Years,
          measuringUnits,
        ] = await Promise.all([
          fuelEnquiryService.lookups.fuelTypes(),
          fuelEnquiryService.lookups.fuelGrades(),
          fuelEnquiryService.lookups.sulfurLimits(),
          fuelEnquiryService.lookups.iso8217Years(),
          fuelEnquiryService.lookups.measuringUnits(),
        ]);
        if (cancelled) return;
        setLookups({ fuelTypes, fuelGrades, sulfurLimits, iso8217Years, measuringUnits });
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('FuelsSpecBlock: failed to load lookups', err);
        setLoadError('Could not load fuels lookups.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [commodityType]);

  // Sprint 7B: ``formFields.products`` is the FULL product list now
  // (Product 1 ... Product N). Pre-Sprint-7B it was extras-only and
  // Product 1 lived as inline scalars on the parent. TradeRecap.js
  // hydration synthesises a single product row from parent scalars
  // when the recap was saved before this refactor (see ``detailToProducts``
  // in TradeRecap.js).
  //
  const products = Array.isArray(formFields.products) ? formFields.products : [];

  if (commodityType !== 'fuels') return null;

  const updateProducts = (mutator) => {
    setFormFields((prev) => {
      const current = Array.isArray(prev.products) ? prev.products : [];
      return { ...prev, products: mutator(current) };
    });
  };

  const handleAdd = () => updateProducts((rows) => [...rows, blankProduct()]);
  const handleRemove = (idx) => updateProducts((rows) => rows.filter((_, i) => i !== idx));

  const handleField = (idx, key) => (e) => {
    const raw = e.target.value;
    let value = raw;
    if ([
      'fuel_type', 'fuel_grade', 'sulfur_limit', 'iso_8217_year',
      'measuring_unit',
    ].includes(key)) {
      value = raw === '' ? null : Number(raw);
    } else if ([
      // Bunkers Phase 1: text CharFields must stay as '' when blank
      // -- DRF CharField rejects null without ``allow_null=True``.
      // ``pricing_type`` is also a CharField but is always 'fixed'/'floating'
      // (driven by the toggle), never blank.
      'supplier_contract_number', 'payment_terms', 'sulfur_limit_other',
      'pricing_type',
    ].includes(key)) {
      value = raw;
    } else if (raw === '') {
      value = null;
    }
    updateProducts((rows) => {
      const next = [...rows];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  // Value-based sibling of ``handleField`` for the amount fields rendered
  // through <AmountInput> (min_tonnage, sale_quantity, sale_price, buy_price,
  // supplier_credit_days). AmountInput already emits the canonical dot-decimal
  // string ('' when cleared), so there's no event to unwrap; blank still maps
  // to null to match the DecimalField "blank -> null" semantics above.
  const handleFieldValue = (idx, key) => (canonical) => {
    const value = canonical === '' ? null : canonical;
    updateProducts((rows) => {
      const next = [...rows];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  // Supplier-pick handler. Mirrors ``SectionSupplierPricing.handleHeadProfile``
  // on the enquiry form so the recap's per-product supplier picker
  // behaves identically: writes the CRM FK + display name, and
  // auto-fills supplier_credit_days from the picked profile's
  // ``partner_credit_days`` when present. Manual edits to credit days
  // still win after the pick (the trader can re-type).
  const handleSupplierProfile = (idx, profile) => {
    updateProducts((rows) => {
      const next = [...rows];
      const row = { ...(next[idx] || {}) };
      row.supplier = profile ? profile.id : null;
      row.supplier_name = profile
        ? (profile.legal_entity_name || profile.trading_short_name || '')
        : '';
      if (
        profile
        && profile.partner_credit_days != null
        && profile.partner_credit_days !== ''
      ) {
        row.supplier_credit_days = String(profile.partner_credit_days);
      }
      next[idx] = row;
      return next;
    });
  };

  // Variant-pick handler. Writes the FK + display cache, then auto-fills
  // the four fuel-spec FKs + measuring_unit from the variant's
  // ``spec_attributes`` and ``unit_id``. Manual edits override.
  const handlePicker = (idx) => (variantId, displayName, fullVariant) => {
    updateProducts((rows) => {
      const next = [...rows];
      const row = { ...(next[idx] || {}) };
      row.stock_item_variant = variantId;
      row.stock_item_variant_display = displayName || '';
      if (variantId != null && fullVariant) {
        const result = autofillSpecFromVariant(fullVariant.spec_attributes, lookups);
        ['fuel_type', 'fuel_grade', 'sulfur_limit', 'iso_8217_year'].forEach((k) => {
          if (result[k] != null) row[k] = result[k];
        });
        // Inline-create variants come back via StockItemVariantSerializer
        // (raw ``unit`` FK), picker selections come back via
        // StockVariantForInvoiceSerializer (flat ``unit_id``). Accept
        // either so the autofill works in both flows.
        const unitId = fullVariant.unit_id ?? fullVariant.unit;
        if (unitId != null) row.measuring_unit = unitId;
      }
      next[idx] = row;
      return next;
    });
  };

  // Bunkers Phase 1: the outer card frame + "Products" header are now
  // provided by the shared <Section> wrapper in TradeRecap.js so the
  // Products section matches Deal Details / Contract & Partners /
  // Commercial Terms visually (dark-blue header + step-2 counter).
  // The inline subtitle was dropped along with the duplicate header
  // because Section has no subtitle slot; the empty-state hint below
  // still tells new traders what to do.
  return (
    <>
      {loadError && <div className="fsb-error" role="alert">{loadError}</div>}

      {products.length === 0 && (
        <div className="fsb-empty-hint">
          No products yet — click "+ Add product" below to add the first fuel.
        </div>
      )}

      {products.map((row, idx) => (
        <ProductCard
          key={`fsb-product-${row.id ?? `new-${idx}`}`}
          row={row}
          idx={idx}
          totalRows={products.length}
          loading={loading}
          lookups={lookups}
          onPicker={handlePicker(idx)}
          onField={(key) => handleField(idx, key)}
          onFieldValue={(key) => handleFieldValue(idx, key)}
          onSupplierProfileChange={(profile) => handleSupplierProfile(idx, profile)}
          onRemove={() => handleRemove(idx)}
          /* ONE "+ Create product" modal lives on the parent screen and is
             shared by every card, so the card must name itself when it opens
             it. Without that the parent can only guess the catalogue
             category, and a Lubes line's new product gets filed as Oil / Gas
             -- wrong category, wrong naming rule. */
          onCreateProduct={onCreateProduct
            ? ((searchTerm) => onCreateProduct(searchTerm, { type: 'fuelsProduct', idx }))
            : undefined}
          recapType={recapType}
          paymentTermsOptions={paymentTermsOptions}
        />
      ))}

      <div className="fsb-add-product-row">
        <button
          type="button"
          className="fsb-add-product-btn"
          onClick={handleAdd}
        >
          + Add product
        </button>
      </div>
    </>
  );
};

const ProductCard = ({
  row, idx, totalRows, loading, lookups,
  onPicker, onField, onFieldValue, onSupplierProfileChange, onRemove, onCreateProduct,
  recapType, paymentTermsOptions,
}) => {
  // Stub blob fed to ``CustomerPicker`` so the chip renders the saved
  // supplier even when only the FK + display name are available
  // (i.e. without an extra CRM round-trip). Mirrors the pattern used
  // by ``SupplierPricingProductBlock`` on the enquiry form.
  const selectedSupplierProfile = row.supplier
    ? { id: row.supplier, legal_entity_name: row.supplier_name || '' }
    : null;
  const isPurchase = recapType === 'PURCHASE';
  // A lubes line carries its identity on the catalogue product, not in a fuel
  // spec. ``productShorthand`` is the one builder the enquiry cards use too,
  // mirroring the server's ``fuel_contract_shorthand``, so a deal keeps its
  // description as it moves down the chain.
  const isLubes = (row.product_kind || 'bunkers') === 'lubes';
  const shorthand = productShorthand(lookups, row);
  // A lubes line has ONE quantity, counted and priced in its own unit -- the
  // same rule as the enquiry card it came from. A fuel line is unchanged.
  const quantityUnit = lineQuantityUnit(row, lookups.measuringUnits);
  const priceLabel = (name) => (isLubes ? perUnitLabel(name, '$', quantityUnit) : `${name} ($/unit)`);
  let quantityLabel = 'Max Tonnage';
  if (isLubes) quantityLabel = quantityUnit ? `Quantity (${quantityUnit})` : 'Quantity';

  return (
    <div className="fsb-product-card">
      <div className="fsb-product-card-header">
        <h3 className="fsb-product-card-title">Product {idx + 1}</h3>
        {totalRows > 1 && (
          <button
            type="button"
            className="fsb-extra-remove"
            onClick={onRemove}
            title="Remove this product"
          >
            Remove
          </button>
        )}
      </div>

      <div className="fsb-kind-toggle" role="group" aria-label="Product type">
        {[
          { key: 'bunkers', label: 'Bunkers' },
          { key: 'lubes', label: 'Lubes' },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            className={`fsb-kind-toggle-btn${
              (row.product_kind || 'bunkers') === opt.key ? ' is-active' : ''
            }`}
            aria-pressed={(row.product_kind || 'bunkers') === opt.key}
            onClick={() => onFieldValue('product_kind')(opt.key)}
            disabled={loading}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {isLubes ? (
        <div className="fsb-grid">
          <div className="fsb-field fsb-field-wide">
            <label className="fsb-label">Lube Product</label>
            <RecapProductSearch
              label=""
              value={row.stock_item_variant_display || ''}
              variantId={row.stock_item_variant || null}
              onChange={onPicker}
              onCreateProduct={onCreateProduct}
              filterFunction={LUBES_VARIANT_FILTER}
              placeholder="Search brand, product name or code..."
              disabled={loading}
            />
          </div>
        </div>
      ) : (
      <div className="fsb-grid">
        <div className="fsb-field">
          <label className="fsb-label">Fuel Type</label>
          <select
            className="fsb-select"
            value={row.fuel_type ?? ''}
            onChange={onField('fuel_type')}
            disabled={loading}
          >
            <option value="">{loading ? 'Loading…' : 'Select…'}</option>
            {lookups.fuelTypes.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div className="fsb-field">
          <label className="fsb-label">Grade</label>
          <select
            className="fsb-select"
            value={row.fuel_grade ?? ''}
            onChange={onField('fuel_grade')}
            disabled={loading}
          >
            <option value="">{loading ? 'Loading…' : 'Select…'}</option>
            {lookups.fuelGrades.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div className="fsb-field">
          <label className="fsb-label">Sulfur Limit</label>
          <select
            className="fsb-select"
            value={row.sulfur_limit ?? ''}
            onChange={onField('sulfur_limit')}
            disabled={loading}
          >
            <option value="">{loading ? 'Loading…' : 'Select…'}</option>
            {lookups.sulfurLimits.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div className="fsb-field">
          <label className="fsb-label">ISO 8217 Year</label>
          <select
            className="fsb-select"
            value={row.iso_8217_year ?? ''}
            onChange={onField('iso_8217_year')}
            disabled={loading}
          >
            <option value="">{loading ? 'Loading…' : 'Select…'}</option>
            {lookups.iso8217Years.map((r) => (
              <option key={r.id} value={r.id}>{r.year}</option>
            ))}
          </select>
        </div>
      </div>
      )}

      {shorthand && (
        <div className="fsb-shorthand-row">
          <span className="fsb-shorthand-label">Contract Shorthand</span>
          <span className="fsb-shorthand-value">{shorthand}</span>
        </div>
      )}

      {/* Bunkers Phase 1 (May 2026): per the PM's recap screenshots the
          per-product quantities are MIN + MAX tonnage (a tolerance
          range), not a single "Sale Quantity". The MAX continues to be
          stored in ``sale_quantity`` (so Total COS / Total Revenue
          rollups don't change), and ``min_tonnage`` is a new nullable
          column. The single price field is LEG-AWARE: SALE recaps
          edit ``sale_price`` (label "Sell Price"); PURCHASE recaps
          edit ``buy_price`` (label "Purchase Price"). Both columns
          continue to be saved on the row -- the hidden leg is
          carried via the shared products array but not surfaced.
          A lubes line has one quantity instead of MIN + MAX, so its row
          has three boxes and is laid out in three columns, not four with
          an empty quarter. */}
      <div className={`fsb-grid${isLubes ? ' fsb-grid-3' : ''}`}>
        <div className="fsb-field">
          <label className="fsb-label">Measuring Unit</label>
          <select
            className="fsb-select"
            value={row.measuring_unit ?? ''}
            onChange={onField('measuring_unit')}
            disabled={loading}
          >
            <option value="">Select…</option>
            {lookups.measuringUnits.map((r) => (
              <option key={r.id} value={r.id}>{unitLabel(r)}</option>
            ))}
          </select>
          {/* Bunkers Phase 1 (May 2026): per-product pricing basis toggle.
              Sits directly under the Measuring Unit. Defaults to Fixed. */}
          <span className="fsb-pricing-label">Pricing Type</span>
          <div
            className="fsb-pricing-toggle"
            role="group"
            aria-label="Pricing type"
          >
            {['fixed', 'floating'].map((opt) => (
              <button
                key={opt}
                type="button"
                className={`fsb-pricing-toggle-btn${
                  (row.pricing_type || 'fixed') === opt ? ' is-active' : ''
                }`}
                aria-pressed={(row.pricing_type || 'fixed') === opt}
                onClick={() => onField('pricing_type')({ target: { value: opt } })}
              >
                {opt === 'fixed' ? 'Fixed' : 'Floating'}
              </button>
            ))}
          </div>
        </div>
        {!isLubes && (
          <div className="fsb-field">
            <label className="fsb-label">Min Tonnage</label>
            <AmountInput
              className="fsb-input"
              placeholder="0"
              value={row.min_tonnage ?? ''}
              onChange={onFieldValue('min_tonnage')}
              maxDecimals={QUANTITY_DECIMALS}
            />
          </div>
        )}
        <div className="fsb-field">
          <label className="fsb-label">{quantityLabel}</label>
          <AmountInput
            className="fsb-input"
            placeholder="0"
            value={row.sale_quantity ?? ''}
            onChange={onFieldValue('sale_quantity')}
            maxDecimals={QUANTITY_DECIMALS}
          />
        </div>
        {isPurchase ? (
          <div className="fsb-field">
            <label className="fsb-label">{priceLabel('Purchase Price')}</label>
            <AmountInput
              className="fsb-input"
              placeholder="0"
              value={row.buy_price ?? ''}
              onChange={onFieldValue('buy_price')}
              maxDecimals={PRICE_DECIMALS}
            />
          </div>
        ) : (
          <div className="fsb-field">
            <label className="fsb-label">{priceLabel('Sell Price')}</label>
            <AmountInput
              className="fsb-input"
              placeholder="0"
              value={row.sale_price ?? ''}
              onChange={onFieldValue('sale_price')}
              maxDecimals={PRICE_DECIMALS}
            />
          </div>
        )}
      </div>

      <div className="fsb-grid">
        <div className="fsb-field">
          <label className="fsb-label">Supplier</label>
          {/* Same CRM search picker the enquiry's Supplier Pricing
              section uses. ``searchParams={}`` searches ALL CRM
              partners (matches the enquiry-side picker) so the recap
              never silently drops a supplier that the trader linked
              upstream. ``allowUnlinked={false}`` because suppliers
              must resolve to a real CRM profile. */}
          <CustomerPicker
            customer={selectedSupplierProfile}
            onChange={onSupplierProfileChange}
            allowUnlinked={false}
            searchParams={{}}
            placeholder="Search CRM partners..."
            noResultsCopy="No matching CRM partners."
          />
        </div>
        <div className="fsb-field">
          <label className="fsb-label">Supplier Credit Days</label>
          <AmountInput
            kind="integer"
            className="fsb-input"
            placeholder="0"
            value={row.supplier_credit_days ?? ''}
            onChange={onFieldValue('supplier_credit_days')}
          />
        </div>
        <div className="fsb-field" style={{ gridColumn: 'span 2' }}>
          <label className="fsb-label">Payment Terms</label>
          <select
            className="fsb-select"
            value={row.payment_terms ?? ''}
            onChange={onField('payment_terms')}
          >
            <option value="">Select…</option>
            {/* withStoredOption: a product saved before the catalogue merge
                holds a clause NAME, not a term code -- without this the
                select renders blank and the agreed term looks lost. */}
            {withStoredOption(paymentTermsOptions, row.payment_terms).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Sprint 7B: blank product row factory. Exported so TradeRecap.js can
// synthesise an initial Product 1 from a legacy single-product recap's
// parent inline scalars on hydration.
export const blankProduct = () => ({
  id: null,
  product_kind: 'bunkers',
  fuel_type: null,
  fuel_grade: null,
  sulfur_limit: null,
  sulfur_limit_other: '',
  iso_8217_year: null,
  measuring_unit: null,
  pricing_type: 'fixed',
  min_tonnage: '',
  sale_quantity: '',
  sale_price: '',
  buy_price: '',
  supplier: null,
  supplier_credit_days: '',
  supplier_contract_number: '',
  payment_terms: '',
  stock_item_variant: null,
  stock_item_variant_display: '',
});

export default FuelsSpecBlock;
